<template>
  <!-- Tables using ant-design-vue's own built-in horizontal scroll
       (:scroll="{ x: ... }") already manage their own scrollbar/DOM
       structure — don't wrap those, just pass everything straight through. -->
  <a-table-core v-if="usesBuiltInScroll" v-bind="$attrs" ref="tableCore">
    <template v-for="(_, slotName) in $slots" #[slotName]="slotProps" :key="slotName">
      <slot :name="slotName" v-bind="slotProps" />
    </template>
  </a-table-core>
  <div v-else>
    <div
      class="table-responsive"
      ref="tableScrollContainer"
      @scroll="onTableScroll"
    >
      <a-table-core v-bind="$attrs" ref="tableCore">
        <template v-for="(_, slotName) in $slots" #[slotName]="slotProps" :key="slotName">
          <slot :name="slotName" v-bind="slotProps" />
        </template>
      </a-table-core>
      <!-- Sits exactly at the container's bottom edge, right where its
           native scrollbar renders, so IntersectionObserver can report the
           precise moment that edge crosses into/out of the viewport —
           tracking the container's own ratio doesn't give that precision
           for a tall table, since it only reports coarse threshold steps. -->
      <div ref="bottomSentinel" class="sticky-hscroll-sentinel"></div>
    </div>
    <div
      v-show="stickyScrollbar.visible"
      class="sticky-hscroll"
      ref="stickyTrack"
      :style="{
        left: stickyScrollbar.left + 'px',
        width: stickyScrollbar.width + 'px',
      }"
      @pointerdown="onTrackPointerDown"
    >
      <div
        class="sticky-hscroll-thumb"
        :class="{ 'is-dragging': isDragging }"
        :style="{
          width: thumb.width + 'px',
          transform: `translateX(${thumb.left}px)`,
        }"
        @pointerdown.stop="onThumbPointerDown"
      ></div>
    </div>
  </div>
</template>

<script>
import { Table } from "ant-design-vue";

// Drop-in replacement for ant-design-vue's <a-table>, registered globally
// under the same name (see main.js) so every existing usage gets a floating
// horizontal scrollbar for free, reachable from anywhere on the page instead
// of only when the table's own native scrollbar happens to be on-screen.
//
// The floating bar is a custom-drawn thumb, not a second real scrollable
// element. Its size/position is derived purely from the live table
// container's own scrollLeft/scrollWidth/clientWidth on every render, and
// dragging it writes straight back to the table's scrollLeft — there's only
// ever one real scroll position, so there's nothing for it to drift out of
// sync with.
export default {
  name: "StickyScrollATable",
  inheritAttrs: false,
  components: {
    "a-table-core": Table,
  },
  data() {
    return {
      stickyScrollbar: {
        visible: false,
        left: 0,
        width: 0,
        hasOverflow: false,
      },
      thumb: {
        width: 0,
        left: 0,
      },
      isDragging: false,
      dragState: null,
      containerInView: false,
      nativeScrollbarInView: false,
    };
  }, 
  computed: {
    usesBuiltInScroll() {
      return this.$attrs.scroll != null;
    },
  },
  mounted() {
    window.addEventListener("resize", this.refreshStickyContentWidth, {
      passive: true,
    });
    this.refreshStickyContentWidth();
    if (this.usesBuiltInScroll) {
      this.suppressRedundantAncestorScroll();
    }
  },
  updated() {
    // columns/data-source are plain props on the wrapped table, so any
    // change that alters its rendered width needs a re-measure — there's no
    // single prop name to watch generically, so nextTick after every
    // re-render is the reliable hook.
    this.$nextTick(this.refreshStickyContentWidth);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.refreshStickyContentWidth);
    if (this.tableResizeObserver) {
      this.tableResizeObserver.disconnect();
    }
    if (this.stickyIntersectionObserver) {
      this.stickyIntersectionObserver.disconnect();
    }
    this.stopDragging();
    this.restoreAncestorScroll();
  },
  methods: {
    // Re-measures table content width only — cheap DOM reads, no
    // layout-forcing getBoundingClientRect(). Safe to call often.
    refreshStickyContentWidth() {
      const container = this.$refs.tableScrollContainer;
      if (!container) return;
      this.ensureStickyObservers(container);
      const table = container.querySelector("table");
      const contentWidth = table ? table.scrollWidth : container.scrollWidth;
      this.stickyScrollbar.hasOverflow =
        contentWidth > container.clientWidth + 1;
      if (!this.stickyScrollbar.hasOverflow) {
        this.stickyScrollbar.visible = false;
      }
      this.updateThumb();
    },
    // Position/visibility comes from IntersectionObserver's own
    // boundingClientRect, computed by the browser off the scroll path —
    // never forces a synchronous layout, so it stays smooth during scroll.
    // Two separate targets are observed: the container itself (is the
    // table on-screen at all) and a 1px sentinel at its bottom edge (has
    // the native scrollbar itself scrolled into view) — using the
    // container's own intersection ratio for both used to leave a stale
    // window (quantized to the threshold step size) where the native
    // scrollbar had already scrolled into view but the floating bar hadn't
    // been told to hide yet, showing both at once on tall tables.
    handleTableIntersection(entries) {
      const container = this.$refs.tableScrollContainer;
      const sentinel = this.$refs.bottomSentinel;
      for (const entry of entries) {
        if (entry.target === container) {
          this.containerInView = entry.isIntersecting;
          this.stickyScrollbar.left = entry.boundingClientRect.left;
          this.stickyScrollbar.width = entry.boundingClientRect.width;
        } else if (entry.target === sentinel) {
          this.nativeScrollbarInView = entry.isIntersecting;
        }
      }
      this.stickyScrollbar.visible =
        this.stickyScrollbar.hasOverflow &&
        this.containerInView &&
        !this.nativeScrollbarInView;
      this.updateThumb();
    },
    // Reads the table container's own scroll metrics directly (cheap
    // property reads — they don't force a layout recalc on their own) so
    // the thumb can never disagree with where the table actually is.
    updateThumb() {
      const container = this.$refs.tableScrollContainer;
      if (!container || !this.stickyScrollbar.hasOverflow) return;
      const trackWidth = this.stickyScrollbar.width || container.clientWidth;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);
      const visibleRatio = scrollWidth > 0 ? clientWidth / scrollWidth : 1;
      const thumbWidth = Math.min(
        Math.max(visibleRatio * trackWidth, 30),
        trackWidth,
      );
      const maxThumbLeft = trackWidth - thumbWidth;
      const scrollRatio =
        maxScrollLeft > 0 ? container.scrollLeft / maxScrollLeft : 0;

      this.thumb.width = thumbWidth;
      this.thumb.left = maxThumbLeft * scrollRatio;
    },
    onTableScroll() {
      this.updateThumb();
    },
    onThumbPointerDown(e) {
      const container = this.$refs.tableScrollContainer;
      if (!container) return;
      e.preventDefault();
      const maxScrollLeft = Math.max(
        container.scrollWidth - container.clientWidth,
        0,
      );
      const trackWidth = this.stickyScrollbar.width || container.clientWidth;
      const maxThumbLeft = trackWidth - this.thumb.width;

      this.dragState = {
        startClientX: e.clientX,
        startScrollLeft: container.scrollLeft,
        maxScrollLeft,
        maxThumbLeft,
      };
      this.isDragging = true;
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", this.onThumbPointerMove);
      window.addEventListener("pointerup", this.stopDragging);
      window.addEventListener("pointercancel", this.stopDragging);
    },
    onThumbPointerMove(e) {
      if (!this.dragState) return;
      const container = this.$refs.tableScrollContainer;
      if (!container) return;
      const { startClientX, startScrollLeft, maxScrollLeft, maxThumbLeft } =
        this.dragState;
      if (maxThumbLeft <= 0) return;
      const deltaX = e.clientX - startClientX;
      const deltaScroll = (deltaX / maxThumbLeft) * maxScrollLeft;
      container.scrollLeft = Math.min(
        Math.max(startScrollLeft + deltaScroll, 0),
        maxScrollLeft,
      );
    },
    stopDragging() {
      this.dragState = null;
      this.isDragging = false;
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", this.onThumbPointerMove);
      window.removeEventListener("pointerup", this.stopDragging);
      window.removeEventListener("pointercancel", this.stopDragging);
    },
    // Clicking the track itself (not the thumb) jumps to that position,
    // matching native scrollbar track-click behavior, then lets the same
    // gesture continue straight into a drag.
    onTrackPointerDown(e) {
      if (e.target !== this.$refs.stickyTrack) return;
      const container = this.$refs.tableScrollContainer;
      if (!container) return;
      const trackRect = this.$refs.stickyTrack.getBoundingClientRect();
      const clickX = e.clientX - trackRect.left;
      const trackWidth = this.stickyScrollbar.width || container.clientWidth;
      const maxScrollLeft = Math.max(
        container.scrollWidth - container.clientWidth,
        0,
      );
      const maxThumbLeft = trackWidth - this.thumb.width;
      const targetThumbLeft = Math.min(
        Math.max(clickX - this.thumb.width / 2, 0),
        maxThumbLeft,
      );
      const ratio = maxThumbLeft > 0 ? targetThumbLeft / maxThumbLeft : 0;
      container.scrollLeft = ratio * maxScrollLeft;
      this.onThumbPointerDown(e);
    },
    // Pages conventionally wrap every <a-table> in their own
    // `.table-responsive` div. For tables using ant-design's built-in
    // `:scroll="{ x }"` horizontal scroll, that outer wrapper is redundant —
    // ant renders its own internal horizontal scrollbar, and if the page's
    // outer div also becomes scrollable, you get two scrollbars stacked at
    // the bottom of the table. Neutralize just that one ancestor, only when
    // it's actually present, rather than a blanket global CSS rule that
    // would also disable scrolling for every other page's own
    // `.table-responsive` wrapper (including this component's own, used
    // for the sticky-thumb case below).
    suppressRedundantAncestorScroll() {
      const ancestor =
        this.$el && this.$el.closest && this.$el.closest(".table-responsive");
      if (!ancestor) return;
      this.suppressedAncestor = ancestor;
      this.suppressedAncestorPrevOverflow = ancestor.style.overflowX;
      ancestor.style.overflowX = "visible";
    },
    restoreAncestorScroll() {
      if (this.suppressedAncestor) {
        this.suppressedAncestor.style.overflowX =
          this.suppressedAncestorPrevOverflow || "";
        this.suppressedAncestor = null;
      }
    },
    // The table can be behind a v-if/v-show (a tab, a modal, an async
    // fetch) at mount time, so observers must attach lazily, the first
    // time refreshStickyContentWidth() runs against a real container.
    ensureStickyObservers(container) {
      if (this.stickyObservedElement === container) return;
      if (this.tableResizeObserver) {
        this.tableResizeObserver.disconnect();
      }
      if (this.stickyIntersectionObserver) {
        this.stickyIntersectionObserver.disconnect();
      }
      this.stickyObservedElement = container;

      if (window.ResizeObserver) {
        this.tableResizeObserver = new ResizeObserver(
          this.refreshStickyContentWidth,
        );
        this.tableResizeObserver.observe(container);
      }
      if (window.IntersectionObserver) {
        this.stickyIntersectionObserver = new IntersectionObserver(
          this.handleTableIntersection,
          { threshold: 0 },
        );
        this.stickyIntersectionObserver.observe(container);
        if (this.$refs.bottomSentinel) {
          this.stickyIntersectionObserver.observe(this.$refs.bottomSentinel);
        }
      }
    },
  },
};
</script>

<style scoped>
.sticky-hscroll-sentinel {
  height: 1px;
}
.sticky-hscroll {
  position: fixed;
  bottom: 0;
  height: 12px;
  background: #eef1f5;
  border-top: 1px solid #dbe3ef;
  z-index: 1030;
  cursor: pointer;
}
.sticky-hscroll-thumb {
  height: 100%;
  background: #d9d9d9;
  border-radius: 6px;
  cursor: grab;
  touch-action: none;
}
.sticky-hscroll-thumb:hover {
  background: #c3c3c3;
}
.sticky-hscroll-thumb.is-dragging {
  background: #b0b0b0;
  cursor: grabbing;
}
</style>
